import { useCallback, memo, useEffect } from 'react';
import { Box, Input, InputGroup, InputLeftElement } from '@chakra-ui/react';
import { Button, Colors, Icon, Text } from '@mercator-gcp/grout';
import { useForm } from 'react-hook-form';
import FilterBadge from 'components/elements/FilterBadge';
import { Text as CKText } from 'components/elements/Text';
import { SearchIcon } from 'components/icons/Search';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { FilterToolbarStyles as styles } from 'components/widgets/FilterToolbar/style';

const subTitleStyle = {
  paddingTop: 'padding_8',
  paddingBottom: 'padding_8',
};

const inputStyle = {
  border: '2px solid',
  borderColor: Colors.ArmyGreen200,
};

const parentColumn = {
  paddingTop: 'padding_4',
};

const inputContainer = { gap: 'padding_8' };

const tagStyles = {
  maxWidth: 'fit-content',
};

const keywordListStyle = {
  gap: 'padding_8',
};

const iconContainer = {
  stroke: Colors.TextQuiet,
  width: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

type FormFields = {
  keyword: string;
};

type KeywordDropdownProps = {
  globalState: string;
  setGlobalState: (value: string) => void;
  pluralText: string;
  singularText: string;
  isExclusion?: boolean;
  onRemoveItem: (value: string) => void;
  placeholder?: string;
  showSearchIcon?: boolean;
  inputLabel: string;
};

const BaseFilterDropdownComponent = ({
  globalState,
  setGlobalState,
  pluralText,
  singularText,
  isExclusion,
  onRemoveItem,
  placeholder,
  showSearchIcon = true,
  inputLabel,
}: KeywordDropdownProps) => {
  const numFilters = globalState.split(',').filter((keyword) => keyword !== '').length;
  const onApply = (values: string, removal = false) => {
    if (removal) {
      onRemoveItem(values);
    } else {
      setGlobalState(values);
    }
  };

  const {
    handleSubmit,
    reset,
    register,
    formState: { isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onChange',
    defaultValues: {
      keyword: '',
    },
  });

  const onSubmit = useCallback(
    ({ keyword }: FormFields) => {
      if (keyword === '') {
        return;
      }
      setGlobalState(keyword);
    },
    [setGlobalState],
  );

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset({
        keyword: '',
      });
    }
  }, [isSubmitSuccessful, reset]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FlexRow sx={{ ...styles.clearAllContainer }}>
        <FlexRow.Left>
          <Text.SmallLabel color={Colors.Black800}>
            {isExclusion ? 'Exclude' : 'Include'}
          </Text.SmallLabel>
        </FlexRow.Left>
        <FlexRow.Right>
          <CKText
            sx={{
              ...styles?.clearAllText,
            }}
            variant="link"
            onClick={() => {
              setGlobalState('');
            }}
          >
            {!isExclusion ? `Clear` : `Remove`} All
          </CKText>
        </FlexRow.Right>
      </FlexRow>
      <FlexColumn sx={parentColumn}>
        <FlexRow
          sx={inputContainer}
          aria-label={isExclusion ? `exclude ${inputLabel}` : `include ${inputLabel}`}
        >
          <InputGroup>
            {showSearchIcon && (
              <InputLeftElement>
                <Box sx={iconContainer}>
                  <SearchIcon />
                </Box>
              </InputLeftElement>
            )}
            <Input
              as="input"
              data-hj-allow
              variant="outline"
              focusBorderColor={Colors.ForestGreen900}
              placeholder={placeholder || 'Add a keyword'}
              sx={inputStyle}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...register('keyword')}
            />
          </InputGroup>
          <Button type="submit">
            <Icon name={Icon.Name.Plus} />
          </Button>
        </FlexRow>

        {numFilters > 0 && (
          <Text.SmallLabel color={Colors.TextQuiet} sx={subTitleStyle}>
            {numFilters} {numFilters > 1 ? pluralText : singularText} added
          </Text.SmallLabel>
        )}
        <FlexColumn sx={keywordListStyle}>
          {globalState
            .split(',')
            .filter((keyword) => keyword !== '')
            .map((currentKeyword) => (
              <Box sx={tagStyles}>
                <FilterBadge
                  label={currentKeyword}
                  onRemove={() => {
                    onApply(currentKeyword, true);
                  }}
                  type={
                    isExclusion
                      ? FilterBadge.BadgeTypes.Exclusion
                      : FilterBadge.BadgeTypes.Filter
                  }
                />
              </Box>
            ))}
        </FlexColumn>
      </FlexColumn>
    </form>
  );
};

export const KeywordDropdown = memo(BaseFilterDropdownComponent);
